// WhatsApp (Meta Cloud API via Embedded Signup, or Kapso): setup, templates, sending, inbound (PRD §2, §5, §7, §9, §13).
import { z } from 'zod';
import * as W_ from './api-whatsapp.js';
import { IdParam, route, W, Ws } from './route-def.js';

/** Binary download (streamed by the handler). */
const Binary = z.unknown();
const Conn = W({ connectionId: IdParam });
const Conv = W({ conversationId: IdParam });

export const whatsappRoutes = {
  getWhatsAppOverview: route({
    method: 'GET', path: '/workspaces/:workspaceId/whatsapp', summary: 'Estado de WhatsApp (Meta o Kapso): conexiones, verificación y plantillas', tag: 'whatsapp',
    minRole: 'manager', params: Ws, response: W_.WhatsAppOverviewSchema,
  }),
  startWhatsAppSetup: route({
    method: 'POST', path: '/workspaces/:workspaceId/whatsapp/setup', summary: 'Crear enlace de configuración de Kapso (coexistencia o dedicado)', tag: 'whatsapp',
    minRole: 'admin', params: Ws, body: W_.StartWhatsAppSetupSchema, response: W_.StartWhatsAppSetupResultSchema,
  }),
  // Browser redirect from Kapso's hosted flow. It only triggers SERVER-SIDE verification, never marks anything connected.
  whatsappSetupCallback: route({
    method: 'GET', path: '/providers/kapso/setup/callback', summary: 'Retorno del flujo de Kapso (redirige a la aplicación)', tag: 'whatsapp',
    public: 'ingress', query: W_.WhatsAppSetupCallbackQuerySchema, response: z.object({}).passthrough(),
  }),
  verifyWhatsAppConnection: route({
    method: 'POST', path: '/workspaces/:workspaceId/whatsapp/connections/:connectionId/verify', summary: 'Verificar con el proveedor (Meta o Kapso) número, cuenta, webhooks y plantillas', tag: 'whatsapp',
    minRole: 'admin', params: Conn, response: W_.WhatsAppConnectionDetailSchema,
  }),
  sendWhatsAppTest: route({
    method: 'POST', path: '/workspaces/:workspaceId/whatsapp/connections/:connectionId/test', summary: 'Prueba de envío a un número de la lista permitida', tag: 'whatsapp',
    minRole: 'admin', params: Conn, body: W_.WhatsAppTestSendSchema, response: W_.WhatsAppTestSendResultSchema,
  }),
  listWhatsAppTemplates: route({
    method: 'GET', path: '/workspaces/:workspaceId/whatsapp/connections/:connectionId/templates', summary: 'Plantillas sincronizadas', tag: 'whatsapp',
    minRole: 'advisor', params: Conn, response: W_.WhatsAppTemplateListSchema,
  }),
  syncWhatsAppTemplates: route({
    method: 'POST', path: '/workspaces/:workspaceId/whatsapp/connections/:connectionId/templates/sync', summary: 'Sincronizar plantillas desde el proveedor (Meta o Kapso)', tag: 'whatsapp',
    minRole: 'admin', params: Conn, response: W_.WhatsAppTemplateListSchema,
  }),
  // Workspace catalog: synced templates of every WhatsApp connection (Meta or Kapso) with filters; honest states.
  listWhatsAppTemplateCatalog: route({
    method: 'GET', path: '/workspaces/:workspaceId/whatsapp/templates', summary: 'Catálogo de plantillas sincronizadas de todas las conexiones de WhatsApp', tag: 'whatsapp',
    minRole: 'advisor', params: Ws, query: W_.WhatsAppTemplateCatalogQuerySchema, response: W_.WhatsAppTemplateCatalogSchema,
  }),
  syncAllWhatsAppTemplates: route({
    method: 'POST', path: '/workspaces/:workspaceId/whatsapp/templates/sync', summary: 'Sincronizar las plantillas de todas las conexiones de WhatsApp', tag: 'whatsapp',
    minRole: 'admin', params: Ws, response: W_.WhatsAppTemplateSyncAllResultSchema,
  }),
  previewWhatsAppTemplate: route({
    method: 'POST', path: '/workspaces/:workspaceId/whatsapp/connections/:connectionId/templates/preview', summary: 'Vista previa con variables', tag: 'whatsapp',
    minRole: 'advisor', params: Conn, body: W_.TemplatePreviewSchema, response: W_.TemplatePreviewResultSchema,
  }),
  setWhatsAppReminderTemplate: route({
    method: 'PUT', path: '/workspaces/:workspaceId/whatsapp/connections/:connectionId/reminder-template', summary: 'Plantilla para recordatorios de cita', tag: 'whatsapp',
    minRole: 'admin', params: Conn, body: z.object({ template: W_.ReminderTemplateSchema.nullable() }), response: W_.WhatsAppConnectionDetailSchema,
  }),
  // Meta Embedded Signup: the state nonce is bound to this workspace + admin; the browser only relays Meta's code + ids.
  startMetaWhatsAppSignup: route({
    method: 'POST', path: '/workspaces/:workspaceId/whatsapp/meta/signup', summary: 'Iniciar «Conectar con Meta» (Embedded Signup): estado de un solo uso + parámetros públicos', tag: 'whatsapp',
    minRole: 'admin', params: Ws, body: W_.StartMetaSignupSchema, response: W_.MetaSignupStartSchema,
  }),
  completeMetaWhatsAppSignup: route({
    method: 'POST', path: '/workspaces/:workspaceId/whatsapp/meta/signup/complete', summary: 'Completar el Embedded Signup: canje del código, suscripción, registro (dedicado) y número', tag: 'whatsapp',
    minRole: 'admin', params: Ws, body: W_.CompleteMetaSignupSchema, response: W_.WhatsAppConnectionDetailSchema,
  }),
  requestMetaWhatsAppHistory: route({
    method: 'POST', path: '/workspaces/:workspaceId/whatsapp/connections/:connectionId/history-sync', summary: 'Coexistencia: volver a solicitar a Meta el historial de chats y contactos de la app', tag: 'whatsapp',
    minRole: 'admin', params: Conn, response: W_.WhatsAppConnectionDetailSchema,
  }),
  getConversationComposer: route({
    method: 'GET', path: '/workspaces/:workspaceId/conversations/:conversationId/whatsapp/composer', summary: 'Qué se puede enviar ahora (ventana de 24 h, plantillas, materiales)', tag: 'inbox',
    minRole: 'advisor', params: Conv, response: W_.ComposerStateSchema,
  }),
  sendConversationMessage: route({
    method: 'POST', path: '/workspaces/:workspaceId/conversations/:conversationId/messages', summary: 'Enviar mensaje, plantilla o material por WhatsApp', tag: 'inbox',
    minRole: 'advisor', params: Conv, body: W_.SendConversationMessageSchema, response: W_.SendMessageResultSchema,
  }),
  sendConversationVoiceNote: route({
    method: 'POST', path: '/workspaces/:workspaceId/conversations/:conversationId/messages/voice-note', summary: 'Enviar una nota de voz grabada o un archivo de audio por WhatsApp (multipart/form-data, campo "file")', tag: 'inbox',
    minRole: 'advisor', params: Conv, body: W_.SendVoiceNoteFieldsSchema, response: W_.SendMessageResultSchema,
  }),
  retryConversationMessage: route({
    method: 'POST', path: '/workspaces/:workspaceId/conversations/:conversationId/messages/:messageId/retry', summary: 'Reintentar un envío fallido (nunca uno incierto)', tag: 'inbox',
    minRole: 'advisor', params: W({ conversationId: IdParam, messageId: IdParam }), response: W_.SendMessageResultSchema,
  }),
  downloadMessageMedia: route({
    method: 'GET', path: '/workspaces/:workspaceId/conversations/:conversationId/messages/:messageId/media', summary: 'Descarga autorizada de un archivo recibido o de una nota de voz enviada', tag: 'inbox',
    minRole: 'advisor', params: W({ conversationId: IdParam, messageId: IdParam }), response: Binary, produces: 'application/octet-stream',
  }),

  // Machine routes: Kapso → us (X-Webhook-Signature HMAC; never cookies) and the scoped media link fallback.
  kapsoWebhook: route({
    method: 'POST', path: '/providers/kapso/:connectionId/webhooks', summary: 'Webhook de Kapso (firmado)', tag: 'ingress',
    public: 'ingress', params: z.object({ connectionId: IdParam }), response: W_.KapsoWebhookAckSchema,
  }),
  // Meta app-level webhook (one URL for every customer): GET verification + POST with X-Hub-Signature-256.
  metaWhatsAppWebhookVerify: route({
    method: 'GET', path: '/providers/meta/whatsapp/webhooks', summary: 'Verificación del webhook de Meta (hub.challenge)', tag: 'ingress',
    public: 'ingress', query: W_.MetaWebhookVerifyQuerySchema, response: z.string(), produces: 'text/plain',
  }),
  metaWhatsAppWebhook: route({
    method: 'POST', path: '/providers/meta/whatsapp/webhooks', summary: 'Webhook de WhatsApp Cloud API (firmado con el secreto de la app)', tag: 'ingress',
    public: 'ingress', response: W_.MetaWebhookAckSchema,
  }),
  kapsoMediaLink: route({
    method: 'GET', path: '/providers/kapso/media/:token', summary: 'Enlace temporal firmado de un material aprobado', tag: 'ingress',
    public: 'ingress', params: z.object({ token: z.string().min(20).max(600) }), response: Binary, produces: 'application/octet-stream',
  }),
} as const;
